/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import React, { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import AllGames from 'src/conponents/Home/AllGames';
import { DEFAULT_CONFIGS } from 'src/constants';
import useSearchQueryParams from 'src/hooks/useSearchQueryParams';
import './index.scss';
import { useLanguageContext } from 'src/context/languageContext';
import useHistory from 'src/hooks/useHistory';
import useSort from 'src/hooks/useSort';
import { useNumber } from 'src/hooks/number';
import useTheme from 'src/hooks/useTheme';
import PropTypes from 'prop-types';
// Brand detection kept for potential future use
import 'src/conponents/Shared/Header';
import { useMarket } from 'src/context/marketContext';
import {
  topGamesByMarket,
  campaignSuggestions,
  campaignTranslations,
  platformStats,
} from 'src/data/market-intelligence';
import ACTIVE_RECTANGLE_GAMES from 'src/data/active-games';
import GAME_RANKINGS from 'src/data/game-rankings';
import MARKET_RANKINGS from 'src/data/market-rankings';
import GameSort from './GameSort';

const otherId = 'others';
const allProviders = {};
const providerMetas = {
  others: { id: otherId, name: 'Others', code: otherId, index: 9999 },
};
const topRcCasualGames = [
  'prosperity-clash', 'crash', 'balonismo',
  'chicken-uncrossable', 'mines', 'pig-mines', 'dragon-crash',
];

// ── Family & theme detection ──
const FAMILY_KEYWORDS = [
  'caramelo', 'prosperity', 'firecrackers fortune',
  'fiesta', 'hades', 'thunder', 'pirates treasure',
  'shapes of fortune', 'big escape',
];
const detectFamily = (name) => {
  const n = (name || '').toLowerCase();
  return FAMILY_KEYWORDS.find((f) => n.includes(f)) || null;
};
const capitalize = (s) => (
  s ? s.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''
);

const MAX_WIN_RANGES = [
  { key: 'all', label: 'Any', min: 0, max: Infinity },
  { key: '0-1000', label: '< 1,000\u00D7', min: 0, max: 1000 },
  { key: '1000-5000', label: '1K\u20135K\u00D7', min: 1000, max: 5000 },
  { key: '5000-10000', label: '5K\u201310K\u00D7', min: 5000, max: 10000 },
  { key: '10000+', label: '10K+\u00D7', min: 10000, max: Infinity },
];


function HomeContainer() {
  const { i18n } = useLanguageContext();
  useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [originalData, setOriginalData] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const { sortBy, sortDirection } = useSort();
  const { round } = useNumber();
  useTheme();
  const { ui, market: headerMarket, lang, version } = useMarket();
  const [searchFocused, setSearchFocused] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── Filter state ──
  const [filterType, setFilterType] = useState('all');
  const [filterVol, setFilterVol] = useState('all');
  const [filterMaxWin, setFilterMaxWin] = useState('all');
  const [filterFamily, setFilterFamily] = useState('all');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterMarket, setFilterMarket] = useState(
    headerMarket === 'global' ? 'all' : headerMarket,
  );

  // Sync with header market selector
  useEffect(() => {
    setFilterMarket(headerMarket === 'global' ? 'all' : headerMarket);
  }, [headerMarket]);

  const l = useSearchQueryParams('l', DEFAULT_CONFIGS.language);
  const values = useSearchQueryParams();
  const [filterBy] = useState(
    values.get('filter_by') || 'all',
  );

  const handleSearchGame = (e) => setSearchTerm(e.target.value);

  // ── Process games (old logic preserved) ──
  const processedGames = useMemo(() => {
    let allGames = [...(gamesData || [])].map((x) => {
      const id = x.provider?.id || x.providerId || otherId;
      if (!allProviders[id]) {
        allProviders[id] = { ...(providerMetas[x.provider?.code || otherId] || {}), ...(x.provider || {}) };
      }
      if (!x.providerId) x.providerId = otherId;
      const popularOffset = allProviders[id]?.popularIndex || 0;
      const popularIndex = x.popularIndex > 0 ? popularOffset + x.popularIndex : x.popularIndex;
      const created = typeof x.created === 'string' ? new Date(x.created) : x.created;
      return { ...x, created, popularIndex, isPopular: popularIndex > 0, type: x.type === 'CRASH' ? 'CASUAL' : x.type, family: detectFamily(x.name) };
    });
    allGames.filter((x) => x.provider?.code === 'rc').sort((a, b) => b.popularIndex - a.popularIndex).forEach((x, i) => { x.isPopular = i < 10; });

    // Old filter_by (provider/type tabs - kept for compatibility)
    if (filterBy && !['all', 'created', 'playCount'].includes(filterBy)) {
      const filtered = allGames.filter((x) => {
        if (filterBy.includes('type|')) return `type|${x.type}` === filterBy;
        return x.providerId === filterBy;
      });
      if (filtered.length > 0) allGames = filtered;
      if (filterBy === 'type|CASUAL') {
        topRcCasualGames.forEach((code, i) => {
          const game = allGames.find((g) => g.code === code);
          if (game) { game.popularIndex = 10000 - i; game.isPopular = true; }
        });
      }
    }
    return allGames;
  }, [gamesData, filterBy]);

  // ── Advanced filters ──
  const displayGames = useMemo(() => {
    let games = [...processedGames];

    // Type filter
    if (filterType !== 'all') {
      games = games.filter((g) => g.type === filterType);
    }
    // Volatility filter
    if (filterVol !== 'all') {
      games = games.filter((g) => (g.volatility || '').toLowerCase() === filterVol);
    }
    // Max win range
    if (filterMaxWin !== 'all') {
      const range = MAX_WIN_RANGES.find((r) => r.key === filterMaxWin);
      if (range) {
        games = games.filter((g) => {
          const mw = Number(g.maxWin) || 0;
          return mw >= range.min && mw < range.max;
        });
      }
    }
    // Family
    if (filterFamily !== 'all') {
      games = games.filter((g) => g.family === filterFamily);
    }
    // Provider
    if (filterProvider !== 'all') {
      games = games.filter((g) => g.provider?.name === filterProvider);
    }

    // Sort
    if (sortBy && sortDirection) {
      const direction = sortDirection;
      if (sortBy === 'popular') {
        // Use market-specific rankings if a market is selected
        const hasMarket = filterMarket !== 'all'
          && MARKET_RANKINGS[filterMarket];
        const ranks = hasMarket
          ? MARKET_RANKINGS[filterMarket]
          : GAME_RANKINGS;
        const maxRank = 9999;
        games.sort((a, b) => {
          const ra = ranks[a.code] || maxRank;
          const rb = ranks[b.code] || maxRank;
          return direction === 'asc' ? rb - ra : ra - rb;
        });
      } else {
        let field = sortBy;
        if (field === 'new') field = 'created';
        const sortFn = (a, b) => (
          direction === 'asc'
            ? a[field] - b[field]
            : b[field] - a[field]
        );
        games.sort(sortFn);
      }
    }
    return games;
  }, [processedGames, filterType, filterVol, filterMaxWin, filterFamily, filterProvider, filterMarket, sortBy, sortDirection]);

  // ── Derived filter options ──
  const filterOptions = useMemo(() => {
    const games = processedGames;
    const vols = [...new Set(games.map((g) => (g.volatility || '').toLowerCase()).filter(Boolean))];
    const families = [...new Set(games.map((g) => g.family).filter(Boolean))];
    const provs = [...new Set(games.map((g) => g.provider?.name).filter(Boolean))];
    const types = [...new Set(games.map((g) => g.type).filter(Boolean))];
    return { vols, families, provs, types };
  }, [processedGames]);

  // ── Active filter count ──
  const activeFilterCount = [
    filterType, filterVol, filterMaxWin, filterFamily, filterProvider, filterMarket,
  ].filter((f) => f !== 'all').length;

  const clearFilters = () => {
    setFilterType('all');
    setFilterVol('all');
    setFilterMaxWin('all');
    setFilterFamily('all');
    setFilterProvider('all');
    setFilterMarket('all');
  };

  // ── Stats ──
  // ── Search ──
  useEffect(() => {
    if (searchTerm !== '') {
      setGamesData(originalData.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase())));
    } else setGamesData(originalData);
  }, [searchTerm]);

  const debouncedResults = React.useMemo(() => debounce(handleSearchGame, 300), []);
  useEffect(() => () => { debouncedResults.cancel(); });

  // ── Fetch from multiple brands to get full portfolio ──
  const fetchData = async (params) => {
    setLoading(false);
    const path = '/api/v1/lobby-available-games';
    const brands = ['rcdemo', 'cademo'];
    const allResults = await Promise.all(
      brands.map((brand) => fetch(
        `${path}?${new URLSearchParams({ ...params, oc: brand })}`,
      ).then((r) => r.json()).catch(() => null)),
    );
    const seen = new Set();
    const games = [];
    allResults.forEach((data) => {
      (data?.data || []).forEach((x) => {
        if (x.status !== 'ACTIVE') return;
        if (!ACTIVE_RECTANGLE_GAMES.has(x.code)) return;
        if (seen.has(x.code)) return;
        seen.add(x.code);
        games.push({
          ...x,
          rtp: round(x.rtp ? (Number(x.rtp) <= 1 && Number(x.rtp * 100)) || Number(x.rtp) : 0, 2),
          maxWin: round(x.maxWin ? Number(x.maxWin) : 0, 0),
        });
      });
    });
    setGamesData(games);
    setOriginalData(games);
  };

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (i18n?.locale) fetchData({ oc: 'rcdemo', t: 'test', l });
    else setLoading(false);
  }, [i18n?.locale]);



  const scrollToGames = () => {
    const el = document.getElementById('games');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`showcase ${loading ? '--loading' : ''}`}>
      {loading && (
        <div className="showcase-loader">
          <div className="loader-spinner">
            <div className="spinner-ring" />
            <div className="spinner-ring" />
            <div className="spinner-ring" />
          </div>
          <span>Loading portfolio...</span>
        </div>
      )}

      {!loading && (
        <>
          {/* ═══ HERO ═══ */}
          <section className="hero">
            <div className="hero__bg" />
            <div className="hero__content">
              {version === 'sweepstakes' && (
                <span className="hero__badge">
                  &#127873; {ui.sweepstakesMode}
                </span>
              )}
              <h1 className="hero__title">
                {ui.heroTitle}
                <br />
                <span className="hero__accent">{ui.heroAccent}</span>
              </h1>
              <p className="hero__subtitle">
                {ui.heroSub}
              </p>
              <div className="hero__stats">
                <div className="hero__stat"><span className="hero__stat-value notranslate">{platformStats.totalGames}</span><span className="hero__stat-label">{ui.statGames}</span></div>
                <div className="hero__stat"><span className="hero__stat-value notranslate">{platformStats.markets}</span><span className="hero__stat-label">{ui.statMarkets}</span></div>
                <div className="hero__stat"><span className="hero__stat-value notranslate">{platformStats.operators}</span><span className="hero__stat-label">{ui.statOperators}</span></div>
                <div className="hero__stat"><span className="hero__stat-value notranslate">{platformStats.uptime}</span><span className="hero__stat-label">{ui.statUptime}</span></div>
              </div>
              <div className="hero__actions">
                <button type="button" className="hero__btn hero__btn--primary" onClick={scrollToGames}>
                  {ui.exploreGames}
                </button>
                <button type="button" className="hero__btn hero__btn--secondary" onClick={() => window.dispatchEvent(new Event('open-contact'))}>
                  {ui.requestInteg}
                </button>
              </div>
            </div>
            {originalData.length > 0 && (
              <div className="hero__visual">
                {originalData.filter((g) => g.preview).slice(0, 5).map((g, i) => (
                  <div key={g.code} className={`hero__game hero__game--${i}`}>
                    <img src={g.preview} alt={g.name} loading="eager" fetchPriority="high" />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ═══ TOP PERFORMING ═══ */}
          {topGamesByMarket[headerMarket] && (
            <section className="top-performing">
              <h2 className="section-title">
                {ui.topPerforming}
              </h2>
              <p className="section-sub">
                {ui.topPerformingSub}
              </p>
              <div className="top-grid">
                {topGamesByMarket[headerMarket]
                  .slice(0, 5)
                  .map((entry) => {
                    const game = originalData.find(
                      (g) => g.code === entry.code,
                    );
                    return (
                      <div
                        key={entry.code}
                        className="top-card"
                      >
                        <span className="top-card__rank">
                          #{entry.rank}
                        </span>
                        {game?.preview && (
                          <div className="top-card__img">
                            <img
                              src={game.preview}
                              alt={game?.name || entry.code}
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="top-card__body">
                          <span className="top-card__name">
                            {game?.name || entry.code}
                          </span>
                          <span className="top-card__stats">
                            {entry.highlight}
                          </span>
                          {entry.insight && (
                            <span className="top-card__insight">
                              {entry.insight}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>
          )}

          {/* ═══ CAMPAIGNS ═══ */}
          {(campaignTranslations[lang]?.[headerMarket] || campaignSuggestions[headerMarket]) && (
            <section className="campaigns">
              <h2 className="section-title">
                {ui.campaigns}
              </h2>
              <p className="section-sub">
                {ui.campaignsSub}
              </p>
              <div className="campaign-grid">
                {(campaignTranslations[lang]?.[headerMarket] || campaignSuggestions[headerMarket]).map((c) => (
                  <div
                    key={c.title}
                    className="campaign-card"
                  >
                    <span className="campaign-card__type">
                      {c.type}
                    </span>
                    <h3 className="campaign-card__title">
                      {c.title}
                    </h3>
                    <p className="campaign-card__desc">
                      {c.desc}
                    </p>
                    {c.story && (
                      <div className="campaign-card__story">
                        <span className="campaign-card__story-label">
                          {ui.operatorStory}
                        </span>
                        <p>{c.story}</p>
                      </div>
                    )}
                    <div className="campaign-card__games">
                      {c.games.map((code) => {
                        const g = originalData.find(
                          (x) => x.code === code,
                        );
                        return g?.preview ? (
                          <img
                            key={code}
                            src={g.preview}
                            alt={g.name}
                            title={g.name}
                          />
                        ) : null;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ═══ GAMES PORTFOLIO ═══ */}
          <section className="portfolio" id="games">
            <div className="portfolio__header">
              <div>
                <h2 className="portfolio__title">{ui.gamePortfolio}</h2>
                <p className="portfolio__sub">{displayGames.length} {ui.gamesAvailable}</p>
              </div>
            </div>

            {/* Search */}
            <div className={`portfolio__search ${searchFocused ? 'focused' : ''}`}>
              <SearchIcon />
              <input
                type="text"
                placeholder={ui.searchGames}
                onChange={debouncedResults}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
            </div>

            {/* ── ADVANCED FILTERS ── */}
            <div className="filters">
              <div className="filters__bar">
                <button
                  type="button"
                  className={`filters__toggle ${filtersOpen ? 'active' : ''}`}
                  onClick={() => setFiltersOpen(!filtersOpen)}
                >
                  <FilterIcon />
                  <span>{ui.filters}</span>
                  {activeFilterCount > 0 && (
                    <span className="filters__count">{activeFilterCount}</span>
                  )}
                </button>

                {activeFilterCount > 0 && (
                  <button type="button" className="filters__clear" onClick={clearFilters}>
                    {ui.clearAll}
                  </button>
                )}

                {/* Active chips */}
                <div className="filters__chips">
                  {filterType !== 'all' && <Chip label={filterType} onRemove={() => setFilterType('all')} />}
                  {filterVol !== 'all' && <Chip label={`${capitalize(filterVol)} Vol`} onRemove={() => setFilterVol('all')} />}
                  {filterMaxWin !== 'all' && <Chip label={MAX_WIN_RANGES.find((r) => r.key === filterMaxWin)?.label} onRemove={() => setFilterMaxWin('all')} />}
                  {filterFamily !== 'all' && <Chip label={capitalize(filterFamily)} onRemove={() => setFilterFamily('all')} />}
                  {filterMarket !== 'all' && <Chip label={`Market: ${capitalize(filterMarket)}`} onRemove={() => setFilterMarket('all')} />}
                </div>

                <div className="filters__sort-wrap">
                  <GameSort />
                </div>
              </div>

              {filtersOpen && (
                <div className="filters__panel">
                  {/* Type */}
                  <FilterGroup label={ui.gameType}>
                    <PillOption active={filterType === 'all'} onClick={() => setFilterType('all')}>All</PillOption>
                    {filterOptions.types.map((tp) => (
                      <PillOption key={tp} active={filterType === tp} onClick={() => setFilterType(tp)}>{capitalize(tp)}</PillOption>
                    ))}
                  </FilterGroup>

                  {/* Volatility */}
                  <FilterGroup label={ui.volatility}>
                    <PillOption active={filterVol === 'all'} onClick={() => setFilterVol('all')}>All</PillOption>
                    {filterOptions.vols.map((v) => (
                      <PillOption key={v} active={filterVol === v} onClick={() => setFilterVol(v)}>{capitalize(v)}</PillOption>
                    ))}
                  </FilterGroup>

                  {/* Max Win */}
                  <FilterGroup label={ui.maxWin}>
                    {MAX_WIN_RANGES.map((r) => (
                      <PillOption key={r.key} active={filterMaxWin === r.key} onClick={() => setFilterMaxWin(r.key)}>{r.label}</PillOption>
                    ))}
                  </FilterGroup>

                  {/* Family */}
                  {filterOptions.families.length > 0 && (
                    <FilterGroup label={ui.gameFamily}>
                      <PillOption active={filterFamily === 'all'} onClick={() => setFilterFamily('all')}>All</PillOption>
                      {filterOptions.families.map((f) => (
                        <PillOption key={f} active={filterFamily === f} onClick={() => setFilterFamily(f)}>{capitalize(f)}</PillOption>
                      ))}
                    </FilterGroup>
                  )}

                  {/* Market */}
                  <FilterGroup label={ui.market}>
                    <PillOption active={filterMarket === 'all'} onClick={() => setFilterMarket('all')}>All</PillOption>
                    <PillOption active={filterMarket === 'brazil'} onClick={() => setFilterMarket('brazil')}>Brasil</PillOption>
                    <PillOption active={filterMarket === 'mexico'} onClick={() => setFilterMarket('mexico')}>Mexico</PillOption>
                    <PillOption active={filterMarket === 'asia'} onClick={() => setFilterMarket('asia')}>Asia</PillOption>
                    <PillOption active={filterMarket === 'chinese'} onClick={() => setFilterMarket('chinese')}>Chinese</PillOption>
                    <PillOption active={filterMarket === 'europe'} onClick={() => setFilterMarket('europe')}>Europe</PillOption>
                  </FilterGroup>

                </div>
              )}
            </div>

            {/* Grid */}
            <AllGames games={displayGames} />

            {displayGames.length === 0 && (
              <div className="portfolio__empty">
                <p>{ui.noGamesFound}</p>
                {activeFilterCount > 0 && (
                  <button type="button" className="filters__clear" onClick={clearFilters}>Clear all filters</button>
                )}
              </div>
            )}
          </section>

          {/* ═══ WHY SECTION ═══ */}
          <section className="why" id="about">
            <h2 className="why__title">
              {ui.whyTitle}
            </h2>
            <p className="why__sub">
              {ui.whySub}
            </p>
            <div className="why__grid">
              <FeatureCard stat="1" statSuffix=" API" title={ui.singleInteg} desc={ui.singleIntegDesc} />
              <FeatureCard stat="50" statSuffix="+" title={ui.currencies} desc={ui.currenciesDesc} />
              <FeatureCard stat="97" statSuffix="%" title={ui.avgRtp} desc={ui.avgRtpDesc} />
              <FeatureCard stat="&lt;50" statSuffix="ms" title={ui.latency} desc={ui.latencyDesc} />
              <FeatureCard stat="99.9" statSuffix="%" title={ui.uptimeTitle} desc={ui.uptimeDesc} />
              <FeatureCard stat="48" statSuffix="h" title={ui.goLive} desc={ui.goLiveDesc} />
            </div>
          </section>

          {/* ═══ FOOTER ═══ */}
          <footer className="site-footer" id="contact">
            <div className="site-footer__cta">
              <h3>{ui.readyTitle}</h3>
              <p>{ui.readyDesc}</p>
              <button type="button" className="site-footer__btn" onClick={() => window.dispatchEvent(new Event('open-contact'))}>
                {ui.contactUs}
              </button>
            </div>
            <div className="site-footer__bottom">
              <span className="site-footer__copy">
                &copy; {new Date().getFullYear()} {ui.copyright}
              </span>
              <span className="site-footer__updated">
                {ui.lastUpdated}
              </span>
            </div>
          </footer>

        </>
      )}
    </div>
  );
}

// ── Sub-components ──

function FilterGroup({ label, children }) {
  return (
    <div className="filter-group">
      <span className="filter-group__label">{label}</span>
      <div className="filter-group__options">{children}</div>
    </div>
  );
}
FilterGroup.propTypes = { label: PropTypes.string, children: PropTypes.node };

function PillOption({ active, onClick, children }) {
  return (
    <button type="button" className={`fpill ${active ? 'active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}
PillOption.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node };

function Chip({ label, onRemove }) {
  return (
    <span className="filter-chip">
      {label}
      <button type="button" onClick={onRemove} aria-label="Remove filter">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
      </button>
    </span>
  );
}
Chip.propTypes = { label: PropTypes.string, onRemove: PropTypes.func };

function FeatureCard({ stat, statSuffix, title, desc }) {
  return (
    <div className="feature-card">
      <div className="feature-card__stat notranslate">
        <span className="feature-card__num">{stat}</span>
        <span className="feature-card__suffix">{statSuffix}</span>
      </div>
      <h3 className="feature-card__title">{title}</h3>
      <p className="feature-card__desc">{desc}</p>
    </div>
  );
}
FeatureCard.propTypes = {
  stat: PropTypes.string, statSuffix: PropTypes.string,
  title: PropTypes.string, desc: PropTypes.string,
};

function SearchIcon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
}

function FilterIcon() {
  return (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" /></svg>);
}

export default HomeContainer;
